import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useDispatch } from "react-redux";

import usePostItem from "@Hooks/usePostItem";
import useGetItem from "@Hooks/useGetItem";
import useDeleteItem from "@Hooks/useDeleteItem";
import { TRANSPORTATION_DOCUMENTS_URL } from "@API/APIRoutes";
import { setSnackbar } from "@Redux/snackBarSlice";
import { LabelStyles, ValueStyles } from "./GenericOrderInfoStyles";
import PDFViewer from "@Components/PDFViewer/PDFViewer";

const REQUIRED_DOCUMENTS = [
  { id: "DELIVERY_NOTE", label: "Delivery Note" },
  { id: "FINAL_CUSTOMS_RELEASE", label: "Final Customs Release" },
];

const formatDocumentLabel = (name) =>
  name
    ? name
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "";

const formatInfoValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }

  if (typeof value === "number" && !Number.isNaN(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().replace("T", " ").split(".")[0];
    }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "N/A";
    }

    const allNumbers = value.every(
      (item) => typeof item === "number" && !Number.isNaN(item)
    );

    if (allNumbers && value.length >= 3) {
      const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] =
        value;

      const date = new Date(
        Date.UTC(
          year,
          month - 1,
          day,
          hour,
          minute,
          second,
          Math.floor(nano / 1e6)
        )
      );

      if (!Number.isNaN(date.getTime())) {
        return date.toISOString().replace("T", " ").split(".")[0];
      }
    }

    const filtered = value
      .filter((item) => item !== null && item !== undefined && item !== "")
      .map((item) => item.toString());

    return filtered.length ? filtered.join(", ") : "N/A";
  }

  if (typeof value === "string" && value.includes("T")) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().replace("T", " ").split(".")[0];
    }
  }

  return value;
};

const infoItemsFactory = ({
  trucksNumbers,
  transportationRemarks,
  timeOfLoading,
  warehouseEntry,
  warehouseExit,
}) => [
  { label: "Trucks Numbers", value: trucksNumbers },
  { label: "Transportation Remarks", value: transportationRemarks },
  { label: "Time Of Loading", value: timeOfLoading },
  { label: "Warehouse Entry", value: warehouseEntry },
  { label: "Warehouse Exit", value: warehouseExit },
];

export default function TransportationSection({
  orderId,
  transportationDocuments = [],
  trucksNumbers,
  transportationRemarks,
  timeOfLoading,
  warehouseEntry,
  warehouseExit,
}) {
  const dispatch = useDispatch();
  const { postItem, loading: postLoading } = usePostItem();
  const getItem = useGetItem();
  const { loading: deleteLoading, deleteItem } = useDeleteItem();

  const numericOrderId = Number(orderId);
  const resolvedOrderId = Number.isNaN(numericOrderId)
    ? orderId
    : numericOrderId;

  const [documents, setDocuments] = useState(transportationDocuments || []);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(null);

  const fileInputRefs = useRef({});

  const documentsMap = useMemo(() => {
    const map = {};
    (documents || []).forEach((doc) => {
      if (doc?.documentName) {
        map[doc.documentName] = doc;
      }
    });
    return map;
  }, [documents]);

  useEffect(() => {
    setDocuments(transportationDocuments || []);
  }, [transportationDocuments]);

  const handleUploadClick = (documentName) => {
    fileInputRefs.current[documentName]?.click();
  };

  const refreshDocuments = async () => {
    const refreshed = await getItem(
      TRANSPORTATION_DOCUMENTS_URL,
      resolvedOrderId
    );
    if (refreshed) {
      setDocuments(refreshed);
    }
  };

  const showSnackbar = (message, error = false) => {
    dispatch(
      setSnackbar({
        open: true,
        message,
        error,
      })
    );
  };

  const handleDeleteDocument = async (documentId) => {
    if (!documentId) {
      showSnackbar("Document identifier is missing.", true);
      return;
    }

    setDeletingDoc(documentId);
    const result = await deleteItem(
      documentId,
      () => {},
      TRANSPORTATION_DOCUMENTS_URL,
      "Transportation document deleted successfully!",
      "Failed to delete transportation document."
    );

    if (result !== false) {
      await refreshDocuments();
    }

    setDeletingDoc(null);
    window.location.reload();
  };

  const handleFileChange = async (documentName, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (file.type !== "application/pdf") {
      showSnackbar("Only PDF files are supported.", true);
      return;
    }

    setUploadingDoc(documentName);

    try {
      const response = await postItem(
        TRANSPORTATION_DOCUMENTS_URL,
        {
          orderId: resolvedOrderId,
          documentName,
        },
        () => {},
        "",
        `Failed to initiate ${formatDocumentLabel(documentName)} upload.`,
        false
      );

      if (!response?.preSignedUrl) {
        throw new Error("Missing upload URL.");
      }

      await axios.put(response.preSignedUrl, file, {
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      await refreshDocuments();
      showSnackbar(
        `${formatDocumentLabel(documentName)} uploaded successfully!`
      );
    } catch (error) {
      console.error("Transportation document upload failed:", error);
      showSnackbar(
        `Failed to upload ${formatDocumentLabel(documentName)}.`,
        true
      );
    } finally {
      setUploadingDoc(null);
      window.location.reload();
    }
  };

  const infoItems = infoItemsFactory({
    trucksNumbers,
    transportationRemarks,
    timeOfLoading,
    warehouseEntry,
    warehouseExit,
  });

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {infoItems.map((item) => (
                <TableCell
                  key={item.label}
                  sx={{
                    ...LabelStyles,
                    fontWeight: 600,
                    textTransform: "none",
                    borderBottom: "2px solid",
                    borderColor: "divider",
                    bgcolor: "background.default",
                  }}
                >
                  {item.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {infoItems.map((item) => {
                const displayValue = formatInfoValue(item.value);
                const isEmpty = displayValue === "N/A";
                return (
                  <TableCell
                    key={item.label}
                    sx={{
                      ...ValueStyles(isEmpty),
                      borderBottom: "none",
                      backgroundColor: "background.paper",
                    }}
                  >
                    {displayValue}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={.5}>
        {REQUIRED_DOCUMENTS.map((doc) => {
          const existingDoc = documentsMap[doc.id];

          return (
            <Grid item xs={12} md={6} key={doc.id}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {doc.label}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{
                        color: existingDoc ? "success.main" : "warning.main",
                        bgcolor: "background.default",
                        border: 1,
                        borderColor: "divider",
                      }}
                      disabled={!existingDoc}
                    >
                      <PictureAsPdfIcon
                        fontSize="small"
                        color={existingDoc ? "success" : "warning"}
                      />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      color="primary"
                      size="small"
                      onClick={() => handleUploadClick(doc.id)}
                      disabled={uploadingDoc === doc.id || postLoading}
                    >
                      {uploadingDoc === doc.id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <UploadFileIcon />
                      )}
                    </Button>
                    {existingDoc ? (
                      <IconButton
                        color="error"
                        onClick={() =>
                          handleDeleteDocument(
                            existingDoc.documentId ?? existingDoc.id
                          )
                        }
                        disabled={
                          deletingDoc ===
                            (existingDoc.documentId ?? existingDoc.id) ||
                          deleteLoading
                        }
                      >
                        {deletingDoc ===
                        (existingDoc.documentId ?? existingDoc.id) ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>

                <input
                  type="file"
                  accept="application/pdf"
                  ref={(ref) => {
                    fileInputRefs.current[doc.id] = ref;
                  }}
                  style={{ display: "none" }}
                  onChange={(event) => handleFileChange(doc.id, event)}
                />

                {existingDoc ? (
                  <Box
                    sx={{
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      backgroundColor: "background.default",
                      p: 1,
                    }}
                  >
                    {existingDoc.presignedUrl ? (
                      <PDFViewer
                        fileUrl={existingDoc.presignedUrl}
                        width="100%"
                        height="60vh"
                        paddingRight="0"
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2 }}
                      >
                        Document uploaded. Preview will be available soon.
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
}

TransportationSection.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  transportationDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      documentName: PropTypes.string,
      presignedUrl: PropTypes.string,
      objectKey: PropTypes.string,
    })
  ),
  trucksNumbers: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
  ]),
  transportationRemarks: PropTypes.string,
  timeOfLoading: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  warehouseEntry: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  warehouseExit: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
};
